import { SolSelectValue, SolTag, SolTagColor, SolText, SolTooltip } from '@sol/components';
import { OwaspClassification } from '../models/owasp-classification';

/** How OWASP spells the identifiers of one of its Top 10 lists. */
interface AsmOwaspListFormat {
  readonly prefix: string;
  readonly padded: boolean;
}

interface AsmOwaspEditionInfo {
  readonly label: string;
  readonly list: string;
  readonly year: number;
  readonly format: AsmOwaspListFormat;
}

/**
 * One classification ready to render: the neutral code tag, the category name the API resolved,
 * and that name as the tag's tooltip. The tooltip carries an empty {@link SolText} when the API
 * resolved no name — `sol-tooltip` renders nothing for empty text, so no guard is needed.
 */
export interface AsmOwaspEntry {
  readonly id: string;
  readonly tag: SolTag;
  readonly name: string | undefined;
  readonly tooltip: SolTooltip;
}

/**
 * Presentation builders for the OWASP Top 10 classifications of a finding, whose category names the
 * API already resolved.
 *
 * OWASP maintains several Top 10 lists in parallel — the web one plus API, Mobile and LLM — and
 * `ObservationClassification` (asm-backend) is unique per (observation, owasp_type, classification),
 * so one finding routinely carries several lists and several positions within one list. "Current"
 * therefore means the newest edition *of each list*: OWASP API 2023 is still the current API Top 10
 * even though the web list moved on to 2025.
 *
 * The position is a rank, not a score, so it renders as a neutral tag.
 */
export class AsmOwaspTools {
  private constructor() {}

  private static readonly TAG_COLOR: SolTagColor = 'grey';

  private static readonly EDITION_PATTERN = /^owasp_(?:(.+)_)?(\d{4})$/;

  /** A code as {@link LIST_FORMATS} spells it: the list prefix, the position, `:` and the year. */
  private static readonly CODE_PATTERN = /^([A-Z]+?)(\d{1,2}):(\d{4})$/;

  /** The web Top 10 has no variant token in its edition key, so it groups under the empty key. */
  private static readonly WEB_LIST = '';

  /**
   * How each OWASP list spells its own identifiers, so a tag can be pasted straight into a search
   * and compared against owasp.org: `A01:2025` (top10.owasp.org), `API1:2023` (api-security.owasp.org),
   * `LLM01:2025` (genai.owasp.org), `M1` (www-project-mobile-top-10). OWASP is not consistent about
   * zero-padding between them, so the padding is per list rather than one rule.
   *
   * Mobile is the single deliberate deviation: OWASP omits the year because only one edition is
   * current, but the overflow popover lists superseded editions too, where a bare `M1` is ambiguous.
   */
  private static readonly LIST_FORMATS: Readonly<Record<string, AsmOwaspListFormat>> = {
    [AsmOwaspTools.WEB_LIST]: { prefix: 'A', padded: true },
    api: { prefix: 'API', padded: false },
    mobile: { prefix: 'M', padded: false },
    llm: { prefix: 'LLM', padded: true },
  };

  private static readonly UNPARSEABLE_LABEL = 'OWASP';

  private static readonly EDITION_SEPARATOR = ':';

  /**
   * Every edition `ObservationClassification.owasp_type` (asm-backend) allows, under the name OWASP
   * gives it, the most recent first — the ones users filter on most: the newest year first, and
   * within a year the web list first. Keep in sync with `OWASP_EDITION_LABELS` there.
   */
  public static readonly EDITION_LABELS: Readonly<Record<string, string>> = {
    owasp_2025: 'OWASP 2025',
    owasp_llm_2025: 'OWASP LLM 2025',
    owasp_mobile_2024: 'OWASP Mobile 2024',
    owasp_api_2023: 'OWASP API 2023',
    owasp_2021: 'OWASP 2021',
    owasp_2017: 'OWASP 2017',
    owasp_2013: 'OWASP 2013',
    owasp_2010: 'OWASP 2010',
    owasp_2007: 'OWASP 2007',
    owasp_2004: 'OWASP 2004',
  };

  /** The newest edition of every list the finding is classified under, newest list first. */
  public static toCurrent(classifications: OwaspClassification[]): AsmOwaspEntry[] {
    const newestYears = AsmOwaspTools.toNewestYears(classifications);
    return AsmOwaspTools.toEntries(
      classifications.filter(classification => AsmOwaspTools.isCurrent(classification, newestYears)),
    );
  }

  /** Every superseded edition the finding is classified under, newest first — what the overflow tooltip lists. */
  public static toPrevious(classifications: OwaspClassification[]): AsmOwaspEntry[] {
    const newestYears = AsmOwaspTools.toNewestYears(classifications);
    return AsmOwaspTools.toEntries(
      classifications.filter(classification => !AsmOwaspTools.isCurrent(classification, newestYears)),
    );
  }

  /**
   * The neutral tag for one OWASP value of a dashboard: an edition key under the name OWASP gives it,
   * anything else as itself — a code such as `A01:2025`, the tag a finding carries for it, or an
   * edition the backend added since.
   */
  public static toValueTag(value: string): SolTag {
    return AsmOwaspTools.toNeutralTag(AsmOwaspTools.EDITION_LABELS[value] ?? value);
  }

  /** Every edition as a select option carrying its key, in {@link EDITION_LABELS} order. */
  public static toEditionValues(): SolSelectValue<SolTag>[] {
    return Object.keys(AsmOwaspTools.EDITION_LABELS).map(edition => ({
      id: edition,
      name: AsmOwaspTools.toValueTag(edition),
    }));
  }

  /**
   * The classifications behind a list of codes such as `A01:2021` or `API1:2023`, as a dashboard
   * returns them, so they render like a finding's. The API resolves no category names for them. A
   * prefix this side does not know reads as a list of the same name, as {@link toFallbackFormat}
   * spells it; a value that is not a code at all is left out.
   */
  public static fromCodes(codes: readonly string[]): OwaspClassification[] {
    return codes
      .map(code => AsmOwaspTools.fromCode(code))
      .filter((classification): classification is OwaspClassification => classification !== undefined);
  }

  /** A classification is current when no newer edition of its own list is present. */
  private static isCurrent(classification: OwaspClassification, newestYears: Record<string, number>): boolean {
    const info = AsmOwaspTools.toEditionInfo(classification.edition);
    return newestYears[info.list] === info.year;
  }

  private static toEntries(classifications: OwaspClassification[]): AsmOwaspEntry[] {
    return AsmOwaspTools.sort(classifications).map(classification => ({
      id: `${classification.edition}-${classification.classification}`,
      tag: AsmOwaspTools.toTag(classification),
      name: classification.name,
      tooltip: SolTooltip.of(classification.name === undefined ? SolText.empty() : SolText.ofRaw(classification.name)),
    }));
  }

  private static toNewestYears(classifications: OwaspClassification[]): Record<string, number> {
    const newestYears: Record<string, number> = {};
    for (const classification of classifications) {
      const info = AsmOwaspTools.toEditionInfo(classification.edition);
      newestYears[info.list] = Math.max(newestYears[info.list] ?? info.year, info.year);
    }
    return newestYears;
  }

  private static toTag(classification: OwaspClassification): SolTag {
    const info = AsmOwaspTools.toEditionInfo(classification.edition);
    const code = AsmOwaspTools.toCode(classification.classification, info.format);
    return AsmOwaspTools.toNeutralTag(`${code}${AsmOwaspTools.EDITION_SEPARATOR}${info.label}`);
  }

  private static fromCode(code: string): OwaspClassification | undefined {
    const match = AsmOwaspTools.CODE_PATTERN.exec(code);
    if (match === null) {
      return undefined;
    }
    const [, prefix, position, year] = match;
    const list =
      Object.keys(AsmOwaspTools.LIST_FORMATS).find(key => AsmOwaspTools.LIST_FORMATS[key].prefix === prefix) ??
      prefix.toLowerCase();
    const edition = list === AsmOwaspTools.WEB_LIST ? `owasp_${year}` : `owasp_${list}_${year}`;
    return { edition, classification: Number(position) };
  }

  private static toNeutralTag(text: string): SolTag {
    return SolTag.of(SolText.ofRaw(text)).withColor(AsmOwaspTools.TAG_COLOR);
  }

  private static toEditionInfo(edition: string): AsmOwaspEditionInfo {
    const match = AsmOwaspTools.EDITION_PATTERN.exec(edition);
    if (match === null) {
      return {
        label: AsmOwaspTools.UNPARSEABLE_LABEL,
        list: edition,
        year: 0,
        format: AsmOwaspTools.LIST_FORMATS[AsmOwaspTools.WEB_LIST],
      };
    }
    const variant: string | undefined = match[1];
    const list = variant ?? AsmOwaspTools.WEB_LIST;
    return {
      label: match[2],
      list,
      year: Number(match[2]),
      format: AsmOwaspTools.LIST_FORMATS[list] ?? AsmOwaspTools.toFallbackFormat(list),
    };
  }

  /** A list the backend adds later: uppercase its key as the prefix, unpadded, so it stays greppable. */
  private static toFallbackFormat(list: string): AsmOwaspListFormat {
    return { prefix: list.split('_').join('').toUpperCase(), padded: false };
  }

  private static toCode(classification: number, format: AsmOwaspListFormat): string {
    const number = format.padded ? String(classification).padStart(2, '0') : String(classification);
    return `${format.prefix}${number}`;
  }

  private static sort(classifications: OwaspClassification[]): OwaspClassification[] {
    return [...classifications].sort((left, right) => {
      const leftInfo = AsmOwaspTools.toEditionInfo(left.edition);
      const rightInfo = AsmOwaspTools.toEditionInfo(right.edition);
      return (
        rightInfo.year - leftInfo.year ||
        leftInfo.list.localeCompare(rightInfo.list) ||
        left.classification - right.classification
      );
    });
  }
}
